/**
 * Aus welchem Topf die Arbeit an diesem Ticket bezahlt wird.
 *
 * Der Anlass: Das Kontingent des Kunden zählte **jede** Zeitbuchung mit. Eine
 * Migration, die der Kunde eigens beauftragt und bezahlt hat, fraß damit sein
 * Betreuungsbudget auf — und im Rückblick sah es aus, als sei die laufende Betreuung
 * aus dem Ruder gelaufen.
 *
 * Bewusst am Ticket und nicht an der einzelnen Zeitbuchung: Ein Ticket ist entweder
 * Pauschale oder beauftragt, nicht halb-halb. Die Buchungen erben die Zuordnung, und
 * der Techniker hakt einmal an statt bei jeder Stunde erneut.
 *
 * Der Regelfall ist die Pauschale und braucht deshalb keinen Klick. Sichtbar bleibt die
 * Zeile trotzdem immer: Wer sie nur bei den Ausnahmen sähe, wüsste nie, dass es sie gibt.
 */
import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { IconCheck, IconInfoCircle, IconReceipt, IconX } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { api } from '../lib/api';
import {
  TICKET_BILLING_SCOPE_LABELS,
  type TicketBillingScope,
} from '../lib/api/domains/weclapp';

const SCOPE_OPTIONS = (Object.keys(TICKET_BILLING_SCOPE_LABELS) as TicketBillingScope[]).map(
  (value) => ({ value, label: TICKET_BILLING_SCOPE_LABELS[value] })
);

/** Wie das Feld für die Referenz heißt — „Nummer" allein sagt nicht, welche. */
const REFERENCE_LABEL: Record<TicketBillingScope, string> = {
  STANDARD: '',
  QUOTE: 'Angebotsnummer',
  PROJECT: 'Projekt (Nummer oder Name)',
};

export function TicketBillingScopeControl({ ticketId }: { ticketId: string }) {
  const queryClient = useQueryClient();
  const { data: result, isLoading } = useQuery({
    queryKey: ['ticket-billing-scope', ticketId],
    queryFn: () => api.getTicketBillingScope(ticketId),
    staleTime: 60_000,
  });
  const data = result?.billingScope;
  const contract = result?.contract ?? null;

  const [editing, setEditing] = useState(false);
  const [scope, setScope] = useState<TicketBillingScope>('STANDARD');
  const [reference, setReference] = useState('');

  // Server-Stand übernehmen, solange nicht bearbeitet wird. Während des Bearbeitens
  // würde ein Refetch sonst die Eingabe unter den Fingern zurücksetzen.
  useEffect(() => {
    if (!data || editing) return;
    setScope(data.scope);
    setReference(data.reference ?? '');
  }, [data, editing]);

  const save = useMutation({
    mutationFn: () =>
      api.setTicketBillingScope(ticketId, {
        scope,
        reference: scope === 'STANDARD' ? null : reference,
      }),
    onSuccess: (result) => {
      queryClient.setQueryData(
        ['ticket-billing-scope', ticketId],
        (previous: { contract: unknown } | undefined) => ({
          billingScope: result.billingScope,
          // Der Vertragshinweis ändert sich hierdurch nicht — den vorhandenen behalten,
          // statt ihn bis zum nächsten Refetch verschwinden zu lassen.
          contract: previous?.contract ?? null,
        })
      );
      // Der Kontingent-Block im Kundenrückblick rechnet anders, sobald sich das ändert.
      queryClient.invalidateQueries({ queryKey: ['customer-report'] });
      // Und die Abzeichen in Listen und Board hängen an derselben Entscheidung.
      queryClient.invalidateQueries({ queryKey: ['ticket-billing-scopes'] });
      setEditing(false);
      notifications.show({
        title: 'Abrechnungsgrundlage gesetzt',
        message:
          result.billingScope.scope === 'STANDARD'
            ? 'Zählt wieder gegen das Kontingent des Kunden.'
            : 'Zählt nicht mehr gegen das Kontingent des Kunden.',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({ title: 'Nicht gespeichert', message: error.message, color: 'red' });
    },
  });

  if (isLoading || !data) return null;

  const referenceMissing = scope !== 'STANDARD' && reference.trim().length === 0;

  const contractHint = contract ? (
    // Steht ÜBER der Zuordnung und ist nicht wegklickbar: Wie der Kunde zahlt, ändert,
    // worauf man bei der Arbeit achtet — das gehört gelesen, bevor die erste Stunde läuft.
    <Alert
      color={contract.color}
      variant="light"
      p="xs"
      icon={<IconInfoCircle size={16} />}
      title={
        <Text size="xs" fw={600}>
          {contract.label}
          {contract.includedHoursPerMonth != null
            ? ` · ${contract.includedHoursPerMonth} h/Monat eingeplant`
            : ''}
        </Text>
      }
    >
      <Text size="xs">{contract.hint}</Text>
    </Alert>
  ) : null;

  if (!editing) {
    return (
      <Stack gap="xs">
        {contractHint}
        <Group gap="xs" justify="space-between" wrap="nowrap">
          <Group gap={6} wrap="nowrap">
            <IconReceipt size={14} style={{ color: 'var(--mantine-color-gray-5)' }} />
            <Text size="xs" c="dimmed">
              Abrechnungsgrundlage:
            </Text>
            <Tooltip
              label={
                data.scope === 'STANDARD'
                  ? 'Diese Zeiten zählen gegen das Betreuungskontingent des Kunden.'
                  : 'Diese Zeiten zählen nicht gegen das Betreuungskontingent — sie sind separat beauftragt.'
              }
              withArrow
              multiline
              w={260}
            >
              <Badge
                size="sm"
                variant={data.scope === 'STANDARD' ? 'light' : 'filled'}
                color={data.scope === 'STANDARD' ? 'gray' : 'indigo'}
              >
                {TICKET_BILLING_SCOPE_LABELS[data.scope]}
                {data.reference ? ` · ${data.reference}` : ''}
              </Badge>
            </Tooltip>
          </Group>
          <Button size="compact-xs" variant="subtle" color="gray" onClick={() => setEditing(true)}>
            Ändern
          </Button>
        </Group>
      </Stack>
    );
  }

  return (
    <Paper withBorder p="sm" radius="md">
      <Stack gap="xs">
        {contractHint}
        <Text size="xs" c="dimmed">
          Nur Zeiten im Regelbetrieb zählen gegen das Betreuungskontingent des Kunden.
          Separat beauftragte Arbeit läuft daran vorbei — und taucht im Rückblick gesondert auf.
          Nicht zu verwechseln mit der Abrechnungsart einer Buchung (abrechenbar, intern,
          Kulanz): Die sagt, OB abgerechnet wird, diese hier, WORAUS.
        </Text>
        <Select
          size="xs"
          label="Abrechnungsgrundlage"
          data={SCOPE_OPTIONS}
          value={scope}
          onChange={(value) => value && setScope(value as TicketBillingScope)}
          allowDeselect={false}
        />
        {scope !== 'STANDARD' && (
          <TextInput
            size="xs"
            label={REFERENCE_LABEL[scope]}
            placeholder={scope === 'QUOTE' ? 'z. B. AN-2026-0042' : 'z. B. Umzug Standort Nord'}
            description="Ohne sie ist im Rückblick nicht prüfbar, worauf sich die Herausnahme stützt."
            value={reference}
            onChange={(e) => setReference(e.currentTarget.value)}
            error={referenceMissing ? 'Erforderlich' : undefined}
          />
        )}
        {data.setByName && !data.isDefault && (
          <Text size="xs" c="dimmed">
            Zuletzt gesetzt von {data.setByName}
            {data.updatedAt ? ` am ${new Date(data.updatedAt).toLocaleDateString('de-DE')}` : ''}.
          </Text>
        )}
        <Group gap="xs" justify="flex-end">
          <Button
            size="compact-xs"
            variant="subtle"
            color="gray"
            leftSection={<IconX size={12} />}
            onClick={() => {
              setEditing(false);
              setScope(data.scope);
              setReference(data.reference ?? '');
            }}
          >
            Abbrechen
          </Button>
          <Button
            size="compact-xs"
            leftSection={<IconCheck size={12} />}
            loading={save.isPending}
            disabled={referenceMissing}
            onClick={() => save.mutate()}
          >
            Übernehmen
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}
